import { t, tf, type Language } from "./i18n"
import { sanitizeHtml } from "./sanitize"

// Debounced caption synchronization across the document
let syncTimer: number | null = null
let currentLang: Language | null = null

// Optional remote caption cache for keys not present in uiText
const remoteCache: Map<Language, Record<string, string>> = new Map()

async function loadRemoteCaptions(lang: Language): Promise<Record<string, string>> {
  if (remoteCache.has(lang)) return remoteCache.get(lang) as Record<string, string>
  try {
    if (lang === "en" || lang === "hi" || lang === "pa") {
      remoteCache.set(lang, {})
      return {}
    }
    const res = await fetch(`/data/captions.${lang}.json`, { cache: "force-cache" })
    if (!res.ok) throw new Error(`Failed to load captions for ${lang}: ${res.status}`)
    const json = await res.json()
    remoteCache.set(lang, json)
    return json
  } catch (e) {
    // Gracefully degrade when remote resources are unavailable
    console.warn("[CaptionSync] remote captions unavailable", lang)
    const empty: Record<string, string> = {}
    remoteCache.set(lang, empty)
    return empty
  }
}

function getCaption(key: string, lang: Language, vars?: Record<string, string>): string {
  // Prefer existing uiText dictionary; fallback to remote cache; then to English; then key
  const base = vars ? tf(key, lang, vars) : t(key, lang)
  if (base && base !== key) return base
  const remote = remoteCache.get(lang)?.[key]
  if (remote) return remote
  return base // may be English fallback or key
}

function updateCaptionElement(el: Element, lang: Language) {
  const key = el.getAttribute("data-caption-key") || ""
  const useHtml = el.getAttribute("data-caption-html") === "true"
  const village = el.getAttribute("data-caption-village") || undefined
  const translated = village ? getCaption(key, lang, { village }) : getCaption(key, lang)
  if (useHtml) {
    try {
      el.innerHTML = sanitizeHtml(translated)
    } catch (e) {
      console.warn("[CaptionSync] failed to apply HTML caption", { key, lang, e })
      // Fallback to textContent on failure
      el.textContent = translated
    }
  } else {
    el.textContent = translated
  }
}

function updateAccessibility(el: Element, lang: Language) {
  const altKey = el.getAttribute("data-alt-key")
  const ariaKey = el.getAttribute("data-aria-label-key")
  if (altKey) {
    const v = getCaption(altKey, lang)
    try { (el as HTMLElement).setAttribute("alt", v) } catch { }
  }
  if (ariaKey) {
    const v = getCaption(ariaKey, lang)
    try { (el as HTMLElement).setAttribute("aria-label", v) } catch { }
  }
}

export async function syncCaptions(lang: Language) {
  currentLang = lang
  await loadRemoteCaptions(lang)
  const captionNodes = Array.from(document.querySelectorAll("[data-caption-key]"))
  for (const el of captionNodes) {
    try {
      updateCaptionElement(el, lang)
    } catch (e) {
      console.warn("[CaptionSync] caption update failed", el, e)
    }
  }
  const accNodes = Array.from(document.querySelectorAll("img[data-alt-key], [data-aria-label-key]"))
  for (const el of accNodes) {
    try {
      updateAccessibility(el, lang)
    } catch (e) {
      console.warn("[CaptionSync] accessibility update failed", el, e)
    }
  }
}

function scheduleSync(lang: Language) {
  if (syncTimer != null) {
    clearTimeout(syncTimer)
    syncTimer = null
  }
  syncTimer = window.setTimeout(() => {
    syncTimer = null
    syncCaptions(lang).catch(() => console.warn("[CaptionSync] sync error"))
  }, 120)
}

export function initCaptionSync() {
  // Initial sync after first paint
  const initialLang = (document.documentElement.lang || "en") as Language
  scheduleSync(initialLang)

  // Respond to app language changes
  window.addEventListener("app:languagechange", (ev: Event) => {
    try {
      const det = (ev as CustomEvent).detail as { lang?: Language } | undefined
      const next = (det?.lang || initialLang) as Language
      scheduleSync(next)
    } catch (e) {
      console.warn("[CaptionSync] languagechange handling failed", e)
    }
  })

  // Keep dynamic content synchronized
  const observer = new MutationObserver((mutations) => {
    // If new nodes with caption keys appear, re-sync lightly
    const needs = mutations.some((m) => {
      for (const n of Array.from(m.addedNodes)) {
        if (!(n instanceof Element)) continue
        if (n.hasAttribute?.("data-caption-key")) return true
        if (n.querySelector?.("[data-caption-key], img[data-alt-key], [data-aria-label-key]")) return true
      }
      return false
    })
    if (needs && currentLang) scheduleSync(currentLang)
  })
  observer.observe(document.body, { childList: true, subtree: true })
}
